import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatusBadge, fmtDate, fmtDKK } from "@/components/shared";
import HeadOffice from "@/pages/HeadOffice";
import LocalDashboard from "@/pages/LocalDashboard";
import TrainerDashboard from "@/pages/TrainerDashboard";
import ClientDashboard from "@/pages/ClientDashboard";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  MapPin, Users, UserRound, Dog, CalendarDays, Ticket, TrendingUp,
  AlertTriangle, Activity, ClipboardList,
} from "lucide-react";

const ADMIN_KPIS = [
  { key: "total_locations", label: "Franchise Locations", icon: MapPin },
  { key: "active_trainers", label: "Active Trainers", icon: Users },
  { key: "active_clients", label: "Active Clients", icon: UserRound },
  { key: "active_dogs", label: "Active Dogs", icon: Dog },
  { key: "upcoming_classes", label: "Upcoming Classes", icon: CalendarDays },
  { key: "total_bookings", label: "Total Bookings", icon: Ticket },
];

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const first = user?.full_name?.split(" ")[0] || "there";
  const isTrainer = user?.role === "trainer";
  const isClient = user?.role === "client";
  const isHeadOffice = ["super_admin", "head_office_admin"].includes(user?.role);
  const isLocal = ["franchise_owner", "location_manager"].includes(user?.role);

  return (
    <div>
      <PageHeader
        title={isHeadOffice ? "HV Head Office" : isLocal ? "Location Dashboard" : isTrainer ? "Trainer Workspace" : `${greeting()}, ${first}`}
        subtitle={isHeadOffice
          ? "Franchise command center — monitor and manage the entire Hundeverket network."
          : isLocal
          ? "Run your local Hundeverket location — classes, trainers, clients, dogs and homework."
          : isTrainer
          ? "Your day: classes, attendance, dogs, notes and homework."
          : "Your training overview and what's coming up next."}
      />
      {isHeadOffice ? <HeadOffice /> : isLocal ? <LocalDashboard /> : isTrainer ? <TrainerDashboard /> : isClient ? <ClientDashboard /> : <AdminDashboard stats={stats} />}
    </div>
  );
}

function AdminDashboard({ stats }) {
  return (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-6">
        {ADMIN_KPIS.map((k) => (
          <Card key={k.key} className="border-border hover:-translate-y-0.5 hover:shadow-sm transition-all" data-testid={`kpi-${k.key}`}>
            <CardContent className="p-5">
              <div className="h-9 w-9 rounded-lg bg-secondary flex items-center justify-center mb-3">
                <k.icon size={18} className="text-primary" strokeWidth={1.5} />
              </div>
              <div className="text-3xl font-heading font-semibold tracking-tight">{stats ? stats[k.key] : "—"}</div>
              <div className="text-xs text-muted-foreground mt-1">{k.label}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="border-border mb-6 bg-primary text-primary-foreground overflow-hidden relative">
        <CardContent className="p-6 flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 text-primary-foreground/70 text-xs uppercase tracking-wide font-semibold">
              <TrendingUp size={14} /> Revenue (placeholder)
            </div>
            <div className="text-4xl font-heading font-semibold mt-2">{stats ? fmtDKK(stats.revenue_placeholder) : "—"}</div>
            <div className="text-primary-foreground/70 text-sm mt-1">Estimated from bookings · full billing coming with HV Payments</div>
          </div>
          <div className="h-20 w-20 rounded-full bg-primary-foreground/10 hidden sm:flex items-center justify-center">
            <TrendingUp size={36} className="text-primary-foreground/60" strokeWidth={1.5} />
          </div>
        </CardContent>
      </Card>

      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="border-border lg:col-span-2">
          <CardHeader><CardTitle className="font-heading text-lg flex items-center gap-2"><CalendarDays size={18} strokeWidth={1.5} /> Upcoming Classes</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {stats?.upcoming?.length ? stats.upcoming.map((c) => (
              <div key={c.id} className="flex items-center justify-between p-3 rounded-lg border border-border/60 hover:bg-muted/40 transition-colors" data-testid="dashboard-upcoming-item">
                <div>
                  <div className="font-medium text-sm">{c.name}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">{c.location_name} · {c.trainer_name}</div>
                </div>
                <div className="text-right"><div className="text-sm font-medium">{fmtDate(c.date)}</div><div className="text-xs text-muted-foreground">{c.time}</div></div>
              </div>
            )) : <p className="text-sm text-muted-foreground py-6 text-center">No upcoming classes.</p>}
          </CardContent>
        </Card>

        <div className="space-y-6">
          <Card className="border-border">
            <CardHeader><CardTitle className="font-heading text-lg flex items-center gap-2"><AlertTriangle size={18} strokeWidth={1.5} className="text-amber-600" /> Needs Attention</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {stats?.needing_attention?.length ? stats.needing_attention.map((l) => (
                <div key={l.id} className="flex items-center justify-between p-2.5 rounded-lg border border-border/60" data-testid="dashboard-attention-item">
                  <span className="text-sm font-medium">{l.name}</span><StatusBadge value={l.status} />
                </div>
              )) : <p className="text-sm text-muted-foreground py-4 text-center">All locations healthy.</p>}
            </CardContent>
          </Card>
          <Card className="border-border">
            <CardHeader><CardTitle className="font-heading text-lg flex items-center gap-2"><Activity size={18} strokeWidth={1.5} /> Recent Activity</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              {stats?.recent_activity?.length ? stats.recent_activity.map((a) => (
                <div key={`${a.text}-${a.at}`} className="flex gap-3 text-sm" data-testid="dashboard-activity-item">
                  <div className="h-2 w-2 rounded-full bg-primary mt-1.5 shrink-0" />
                  <div><div className="text-foreground">{a.text}</div><div className="text-xs text-muted-foreground">{fmtDate(a.at)}</div></div>
                </div>
              )) : <p className="text-sm text-muted-foreground py-4 text-center">No recent activity.</p>}
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
}

function PersonalDashboard({ stats, role }) {
  const kpis = [
    { key: "active_dogs", label: role === "client" ? "My Dogs" : "Assigned Dogs", icon: Dog },
    { key: "upcoming_classes", label: "Upcoming Classes", icon: CalendarDays },
    { key: "homework_open", label: "Open Homework", icon: ClipboardList },
    { key: "total_bookings", label: "Bookings", icon: Ticket },
  ];
  return (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {kpis.map((k) => (
          <Card key={k.key} className="border-border" data-testid={`kpi-${k.key}`}>
            <CardContent className="p-5">
              <div className="h-9 w-9 rounded-lg bg-secondary flex items-center justify-center mb-3"><k.icon size={18} className="text-primary" strokeWidth={1.5} /></div>
              <div className="text-3xl font-heading font-semibold tracking-tight">{stats ? stats[k.key] : "—"}</div>
              <div className="text-xs text-muted-foreground mt-1">{k.label}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="border-border">
          <CardHeader><CardTitle className="font-heading text-lg flex items-center gap-2"><Dog size={18} strokeWidth={1.5} /> {role === "client" ? "My Dogs" : "Assigned Dogs"}</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {stats?.my_dogs?.length ? stats.my_dogs.map((d) => (
              <Link key={d.id} to="/dogs" className="flex items-center justify-between p-3 rounded-lg border border-border/60 hover:bg-muted/40 transition-colors" data-testid="dashboard-dog-item">
                <div><div className="font-medium text-sm">{d.name}</div><div className="text-xs text-muted-foreground">{d.breed} · {d.location_name}</div></div>
                <StatusBadge value={d.current_level} />
              </Link>
            )) : <p className="text-sm text-muted-foreground py-4 text-center">No dogs yet.</p>}
          </CardContent>
        </Card>

        <div className="space-y-6">
          <Card className="border-border">
            <CardHeader><CardTitle className="font-heading text-lg flex items-center gap-2"><CalendarDays size={18} strokeWidth={1.5} /> Upcoming Classes</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {stats?.upcoming?.length ? stats.upcoming.map((c) => (
                <div key={c.id} className="flex items-center justify-between p-3 rounded-lg border border-border/60" data-testid="dashboard-upcoming-item">
                  <div><div className="font-medium text-sm">{c.name}</div><div className="text-xs text-muted-foreground">{c.location_name}</div></div>
                  <div className="text-right"><div className="text-sm font-medium">{fmtDate(c.date)}</div><div className="text-xs text-muted-foreground">{c.time}</div></div>
                </div>
              )) : <p className="text-sm text-muted-foreground py-4 text-center">No upcoming classes.</p>}
            </CardContent>
          </Card>
          <Card className="border-border">
            <CardHeader><CardTitle className="font-heading text-lg flex items-center gap-2"><ClipboardList size={18} strokeWidth={1.5} /> Recent Homework</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {stats?.recent_homework?.length ? stats.recent_homework.map((h) => (
                <Link key={h.id} to="/homework" className="flex items-center justify-between p-3 rounded-lg border border-border/60 hover:bg-muted/40 transition-colors" data-testid="dashboard-homework-item">
                  <span className="text-sm font-medium">{h.title}</span><StatusBadge value={h.status} />
                </Link>
              )) : <p className="text-sm text-muted-foreground py-4 text-center">No homework.</p>}
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
}
